import { useEffect } from 'react'
import { Alert, Form, Input, Modal, Select, Switch, Typography } from 'antd'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { submitOnValid } from '@/shared/lib/submitOnValid'
import {
  createOrgNodeType,
  ORG_CAPABILITIES,
  orgTypeName,
  updateOrgNodeType,
  type OrgNodeType,
} from './orgApi'

/** `org_node_type_code_shape` ning nusxasi — server va baza aynan shuni talab qiladi */
const TYPE_CODE = /^[A-Z][A-Z0-9_]{1,31}$/

interface OrgNodeTypeFormModalProps {
  open: boolean
  /** `null` = yangi tur */
  type: OrgNodeType | null
  types: readonly OrgNodeType[]
  onClose: () => void
}

interface FormValues {
  code: string
  name: string
  costCenter: boolean
  holdsStock: boolean
  settlementParty: boolean
  hasEmployees: boolean
  allowedParents: string[]
}

/**
 * Birlik TURINI ochish va tahrirlash — `QARORLAR.md` §19.3.
 *
 * <h2>Bu formaning butun mohiyati</h2>
 * Administrator "Mintaqa" turini ochadi, unga "xarajat markazi"
 * imkoniyatini beradi — va moliya uni darhol o'lchov sifatida ko'radi,
 * bitta qator kod o'zgarmasdan, deploy qilinmasdan.
 *
 * <h2>⚠️ Shuning uchun bu forma "sozlama" emas, QOIDA yozish</h2>
 * Har bayroq biznes xulqini belgilaydi. "is_cost_center" degan yozuv
 * operatorga hech narsa aytmaydi, shuning uchun har birining ostida
 * NIMA BO'LISHINI aytadigan matn turadi (`extra`).
 *
 * <h2>⚠️ KOD O'ZGARMAYDI</h2>
 * Tahrirlashda maydon qulflangan. Kod ikki joyda ishora sifatida
 * yashaydi — `org_node.kind` da va boshqa turlarning `allowedParents`
 * massivida — va ikkinchisini hech kim yangilamaydi. Serverda ham shu
 * qoida bor (400 + sabab); bu yerdagi qulf uni faqat KO'RSATADI.
 */
export function OrgNodeTypeFormModal({
  open,
  type,
  types,
  onClose,
}: OrgNodeTypeFormModalProps) {
  const { t } = useTranslation()
  const [form] = Form.useForm<FormValues>()
  const queryClient = useQueryClient()

  const isEdit = type !== null
  const locked = type?.system === true

  useEffect(() => {
    if (!open) return
    form.setFieldsValue(
      type
        ? {
            code: type.code,
            name: type.name,
            costCenter: type.costCenter,
            holdsStock: type.holdsStock,
            settlementParty: type.settlementParty,
            hasEmployees: type.hasEmployees,
            allowedParents: [...type.allowedParents],
          }
        : {
            code: '',
            name: '',
            costCenter: false,
            holdsStock: false,
            settlementParty: false,
            hasEmployees: false,
            allowedParents: [],
          },
    )
  }, [open, type, form])

  const save = useMutation({
    mutationFn: (values: FormValues) => {
      const draft = {
        code: values.code.trim().toUpperCase(),
        name: values.name.trim(),
        costCenter: values.costCenter,
        holdsStock: values.holdsStock,
        settlementParty: values.settlementParty,
        hasEmployees: values.hasEmployees,
        allowedParents: values.allowedParents,
      }
      return isEdit && type ? updateOrgNodeType(type.id, draft) : createOrgNodeType(draft)
    },
    onSuccess: () => {
      // ⚠️ `org.all` PREFIKS: turlar ro'yxati ham, DARAXT ham eskiradi.
      // Daraxt tugunlarni turlar bilan bog'lab chizadi — imkoniyat
      // o'zgargach, o'sha tugunlarning kartochkasi boshqa maydonlarni
      // ko'rsatishi kerak.
      void queryClient.invalidateQueries({ queryKey: queryKeys.org.all })
      form.resetFields()
      onClose()
    },
  })

  /** Bekor qilib chiqish — xato xabari ham qolmasin: keyingi ochilishda u
      eski va begona bo'lardi */
  const close = () => {
    save.reset()
    onClose()
  }

  return (
    <Modal
      open={open}
      title={isEdit ? t('org.type.editTitle') : t('org.type.createTitle')}
      onCancel={close}
      onOk={() => submitOnValid(form, (values) => save.mutate(values))}
      confirmLoading={save.isPending}
      okText={t('common.save')}
      cancelText={t('common.cancel')}
      destroyOnHidden
      width={680}
    >
      {/* Xato AYNAN shu yerda qoladi — sahifa bannerida emas: oyna ochiqligicha
          turadi va niqob ortidagi xabarni operator umuman ko'rmasdi */}
      {save.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={translateError(save.error, t)}
        />
      ) : null}

      <Form<FormValues> form={form} layout="vertical" preserve={false} disabled={save.isPending}>
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('org.type.formNote')}
        />

        {locked ? (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message={t('org.type.systemLocked')}
          />
        ) : null}

        {isEdit && !locked && type.usedBy > 0 ? (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message={t('org.type.inUseWarning', { count: type.usedBy })}
          />
        ) : null}

        <Form.Item
          name="name"
          label={t('org.type.name')}
          extra={t('org.type.nameHint')}
          rules={[
            { required: true, message: t('org.type.validation.nameRequired') },
            { max: 160, message: t('org.type.validation.nameLength') },
          ]}
        >
          <Input autoFocus />
        </Form.Item>

        <Form.Item
          name="code"
          label={t('org.type.code')}
          extra={isEdit ? t('org.type.codeLockedHint') : t('org.type.codeHint')}
          rules={[
            { required: true, message: t('org.type.validation.codeRequired') },
            { pattern: TYPE_CODE, message: t('org.type.validation.codeFormat') },
          ]}
        >
          <Input disabled={isEdit} placeholder="REGION" />
        </Form.Item>

        <Typography.Title level={5} style={{ marginTop: 8 }}>
          {t('org.type.capabilitiesTitle')}
        </Typography.Title>
        <Typography.Paragraph type="secondary">
          {t('org.type.capabilitiesHint')}
        </Typography.Paragraph>

        {ORG_CAPABILITIES.map((capability) => (
          <Form.Item
            key={capability}
            name={capability}
            label={t(`org.capability.${capability}.label`)}
            extra={t(`org.capability.${capability}.hint`)}
            valuePropName="checked"
          >
            <Switch disabled={locked} />
          </Form.Item>
        ))}

        {/*
          ⚠️ Variantlar `code` maydoniga BOG'LIQ, shuning uchun
          `shouldUpdate` bilan qayta chiziladi. Sabab pastda.
        */}
        <Form.Item
          noStyle
          shouldUpdate={(prev: { code?: string }, next: { code?: string }) =>
            prev.code !== next.code
          }
        >
          {({ getFieldValue }) => {
            const own = String(getFieldValue('code') ?? '')
              .trim()
              .toUpperCase()
            const options = types.map((item) => ({
              value: item.code,
              label: `${orgTypeName(item, t)} (${item.code})`,
            }))
            /*
              ⚠️ Turning O'ZI ham ro'yxatda bo'lishi mumkin va bu xato
              emas: "filial filial ostida" odatiy hol (§19.2 — chuqurlik
              cheklanmagan). Yaratish paytida u hali JADVALDA YO'Q, ya'ni
              variant faqat kiritilayotgan koddan olinadi. Serverda ham
              aynan shu chetlanish bor (`requireKnownParents`:
              "known.add(draft.code())") — busiz yangi turni o'zi ostiga
              joylashtirish uchun ikki qadamli tahrir kerak bo'lardi.
            */
            if (TYPE_CODE.test(own) && !types.some((item) => item.code === own)) {
              options.push({ value: own, label: t('org.type.selfParentOption', { code: own }) })
            }

            return (
              <Form.Item
                name="allowedParents"
                label={t('org.type.allowedParents')}
                extra={t('org.type.allowedParentsHint')}
              >
                <Select
                  mode="multiple"
                  allowClear
                  disabled={locked}
                  optionFilterProp="label"
                  placeholder={t('org.type.allowedParentsPlaceholder')}
                  options={options}
                />
              </Form.Item>
            )
          }}
        </Form.Item>
      </Form>
    </Modal>
  )
}
